import { DELIVERED } from "../../constants";
import { IDoorDash } from "../../models/tracking";

type DoorDashLike = Partial<IDoorDash> & { dasher_status?: string };

const hasCoordValue = (value: unknown): value is string | number =>
  value !== null && value !== undefined && value !== "";

/** Parse API lat/lng (strings or null) into Radar-ready [lng, lat]. Never treats null as 0. */
export function getDoorDashLngLat(
  doorDash?: Partial<IDoorDash> | null
): [number, number] | null {
  if (!doorDash) return null;
  if (!hasCoordValue(doorDash.lat) || !hasCoordValue(doorDash.lng)) {
    return null;
  }

  const lat = Number(doorDash.lat);
  const lng = Number(doorDash.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return [lng, lat];
}

export const isDoorDashDelivered = (doorDash?: DoorDashLike | null): boolean => {
  if (!doorDash) return false;
  const candidates = [doorDash.status, doorDash.dasher_status];
  return candidates.some(
    (value) => String(value ?? "").toLowerCase() === DELIVERED
  );
};

/** Coords for the map marker only — hidden when delivered or coords invalid. */
export function getDoorDashMapCoords(
  doorDash?: DoorDashLike | null
): [number, number] | null {
  if (isDoorDashDelivered(doorDash)) return null;
  return getDoorDashLngLat(doorDash);
}

/**
 * Merge order + poll for UI. When a poll payload exists, only its coords count
 * (no order fallback while API says lat/lng null).
 */
export function mergeDoorDashTracking(
  orderDelivery?: Partial<IDoorDash> | null,
  polled?: Partial<IDoorDash> | null
): IDoorDash {
  const merged = {
    ...(orderDelivery || {}),
    ...(polled || {}),
  } as IDoorDash;

  const coords =
    polled != null
      ? getDoorDashLngLat(polled)
      : getDoorDashLngLat(orderDelivery);

  if (!coords) {
    return { ...merged, lat: undefined, lng: undefined };
  }

  const [lng, lat] = coords;
  return { ...merged, lat, lng };
}

/** Apply doordash poll; null lat/lng from API clears coords (no 0,0, no stale pin). */
export function mergeDasherPollUpdate(
  prev: Partial<IDoorDash> | null | undefined,
  next: Partial<IDoorDash>
): IDoorDash {
  const merged = { ...(prev || {}), ...next } as IDoorDash;
  const coords = getDoorDashLngLat(next);

  if (coords) {
    return { ...merged, lat: coords[1], lng: coords[0] };
  }

  return { ...merged, lat: undefined, lng: undefined };
}
